import React, { useState, useEffect } from 'react';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ArrowPathIcon,
  InformationCircleIcon,
  EyeIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { getCertificateTemplate, updateCertificateTemplate } from '../../services/certificateService';
import { generateCertificate } from '../../utils/certificateGenerator';
import { CertificateTemplate, Application } from '../../types';

const CertificateTemplateEditor: React.FC = () => {
  const [template, setTemplate] = useState<CertificateTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    setLoading(true);
    const data = await getCertificateTemplate();
    setTemplate(data);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    setMessage(null);
    try {
      await updateCertificateTemplate(template);
      setMessage({ type: 'success', text: 'Template updated successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update template.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!template) return;
    
    // Create a mock application for preview
    const mockApp: Partial<Application> = {
      id: 'PREVIEW-123',
      firstName: 'SAUMYA',
      lastName: 'RANJAN',
      submittedAt: new Date(),
    };

    try {
      const doc = await generateCertificate(mockApp as Application, template);
      doc.save('certificate-preview.pdf');
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Error generating preview. Please check console.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!template) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <DocumentTextIcon className="w-6 h-6 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Certificate Template Editor</h3>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handlePreviewPdf}
            className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-200 hover:border-primary-200"
          >
            <EyeIcon className="w-4 h-4" />
            <span>Preview PDF</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {saving ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <CheckCircleIcon className="w-4 h-4" />}
            <span>{saving ? 'Saving...' : 'Save Template'}</span>
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircleIcon className="w-5 h-5" /> : <InformationCircleIcon className="w-5 h-5" />}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-xs uppercase tracking-wider">Organization Name</label>
              <input
                type="text"
                value={template.organizationName}
                onChange={(e) => setTemplate({ ...template, organizationName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-xs uppercase tracking-wider">Certificate Title</label>
              <input
                type="text"
                value={template.title}
                onChange={(e) => setTemplate({ ...template, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-xs uppercase tracking-wider">Course / Program Name</label>
            <input
              type="text"
              value={template.subTitle}
              onChange={(e) => setTemplate({ ...template, subTitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
              placeholder="e.g. Sports Excellence Program"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-xs uppercase tracking-wider">Signatory Name</label>
            <input
              type="text"
              value={template.issuedBy}
              onChange={(e) => setTemplate({ ...template, issuedBy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 text-xs uppercase tracking-wider">Footer / Legal Text</label>
            <input
              type="text"
              value={template.footerText}
              onChange={(e) => setTemplate({ ...template, footerText: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>
        </div>

        {/* Premium Live Preview */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-blue-600 rounded-xl blur opacity-10 group-hover:opacity-20 transition duration-1000 group-hover:duration-200"></div>
          <div className="relative bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden aspect-[1.414/1] flex flex-col p-8 items-center justify-center text-center">
            {/* Mock Frame UI */}
            <div className="absolute top-0 left-0 w-24 h-24 border-t-8 border-l-8 border-primary-600/20 rounded-tl-xl"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 border-b-8 border-r-8 border-orange-500/20 rounded-br-xl"></div>
            
            <div className="mb-4">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                 <TrophyIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h4 className="text-xl font-serif font-bold text-primary-800 tracking-tight">{template.organizationName.toUpperCase() || 'KREED ODISHA'}</h4>
              <div className="h-px w-32 bg-gradient-to-r from-transparent via-primary-300 to-transparent mx-auto mt-2"></div>
            </div>

            <h5 className="text-[10px] font-bold text-blue-600 uppercase tracking-[0.2em] mb-4">{template.title || 'TRAINING COMPLETION CERTIFICATE'}</h5>
            
            <p className="text-[8px] text-gray-400 italic mb-2">This is to certify that</p>
            <h6 className="text-2xl font-serif italic font-bold text-primary-900 mb-2">YOUR NAME</h6>
            <div className="h-0.5 w-48 bg-orange-200 mx-auto mb-4"></div>

            <p className="text-[8px] text-gray-500 mb-1">has successfully completed the training program on</p>
            <p className="text-sm font-bold text-primary-700 mb-1">"{template.subTitle || 'Course Name'}"</p>
            <p className="text-[8px] text-gray-500 mb-6">conducted by {template.organizationName}.</p>

            <div className="w-full flex justify-between items-end mt-4 px-8">
               <div className="text-left">
                  <div className="text-[7px] font-bold text-gray-400 uppercase">Date of Completion</div>
                  <div className="text-[9px] font-bold text-primary-900 border-b border-gray-200 pb-0.5">{new Date().toLocaleDateString()}</div>
               </div>

               <div className="w-12 h-12 bg-yellow-50 rounded-full border-4 border-yellow-200 flex items-center justify-center">
                  <div className="text-[6px] font-black text-yellow-600 leading-tight">WELL<br/>DONE!</div>
               </div>

               <div className="text-right">
                  <div className="h-6 w-16 border-b border-gray-200 mb-1 flex items-center justify-end">
                     <span className="text-[8px] font-serif italic text-blue-400 opacity-50">Signature</span>
                  </div>
                  <div className="text-[7px] font-bold text-gray-900 uppercase">{template.issuedBy || 'Authorized Signatory'}</div>
                  <div className="text-[6px] text-gray-400 uppercase">{template.organizationName}</div>
               </div>
            </div>

            <div className="absolute bottom-4 right-4 text-[6px] text-gray-300">
              Certificate ID: PREVIEW-XXXX
            </div>
          </div>
          <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500 italic">
            <InformationCircleIcon className="w-4 h-4" />
            <span>Interactive preview. Use "Preview PDF" to see final high-fidelity export.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplateEditor;
